import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeMapStateToProps} from '../../helpers/reselectHelpers';
import {AppState} from '../../store/appState';
import {makeGetReplyChannel} from '../../store/data/channels/channelsSelectors';
import {
  makeGetConversationActionContext,
  makeGetConversationIsSpam,
} from '../../store/data/conversations/conversationsSelectors';
import {getConversations, getMessages} from '../../store/data/dataSelectors';
import {makeGetMessageNamespace} from '../../store/data/messages/messagesSelectors';
import {getUser, getUserId} from '../../store/data/teammates/teammatesSelectors';
import {CellsContext, makeDefaultCellsContext} from '../../store/panels/cells/context/cellsContext';
import {MessagePanelId} from '../../store/panels/panelStateBase';
import {PopupMessageViewer} from './popupMessageViewer';

/*
 * Props.
 */

interface PopupMessageViewerContainerProps {
  panelId: MessagePanelId;
  messageUid: string;
  conversationId: number;
  cellsContext?: CellsContext;
  initialHeight?: number;
}

/*
 * Selectors.
 */

function getMessageUid(_state: AppState, props: PopupMessageViewerContainerProps) {
  return props.messageUid;
}

function getConversationId(_state: AppState, props: PopupMessageViewerContainerProps) {
  return props.conversationId;
}

function getPanelId(_state: AppState, props: PopupMessageViewerContainerProps) {
  return props.panelId;
}

function getCellsContext(_state: AppState, props: PopupMessageViewerContainerProps) {
  return props.cellsContext;
}

function makeGetCellsContextWithDefault() {
  return createSelector(
    [getCellsContext, getUser],
    (maybeCellsContext, user) => maybeCellsContext || makeDefaultCellsContext(user, false),
  );
}

function makeGetSelectedInboxId() {
  return createSelector([makeGetCellsContextWithDefault()], (cellsContext) => cellsContext.inboxId);
}

function makeGetMessage() {
  return createSelector([getMessageUid, getMessages], (messageUid, messages) => messages[messageUid]);
}

function makeGetConversation() {
  return createSelector(
    [getConversationId, getConversations],
    (conversationId, conversations) => conversations[conversationId],
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<PopupMessageViewerContainerProps>()(() => {
  const getMessage = makeGetMessage();
  const getConversation = makeGetConversation();
  const getNamespace = makeGetMessageNamespace(getMessage);
  const getSelectedInboxId = makeGetSelectedInboxId();
  const getReplyChannel = makeGetReplyChannel({getMessage, getSelectedInboxId});
  const getCellsContextWithDefault = makeGetCellsContextWithDefault();

  return {
    userId: getUserId,
    message: getMessage,
    namespace: getNamespace,
    isSpam: makeGetConversationIsSpam(getConversation),
    panelId: getPanelId,
    replyChannel: getReplyChannel,
    cellsContext: getCellsContextWithDefault,
    conversationActionContext: makeGetConversationActionContext(getCellsContextWithDefault, getConversation),
  };
});

export const PopupMessageViewerContainer = connect(mapStateToProps)(PopupMessageViewer);
